import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetectorModule from 'i18next-browser-languagedetector';

// English is the fallback language and must always be available
// synchronously, so it stays bundled. Every other locale (~175-190KB each,
// ~2MB total) is loaded on demand via the lazy backend below — only the
// language the user actually selects is fetched.
import en from './resources/en.json';

// Maps an i18next language code to a dynamic import of its JSON resource.
// Note the fun-variant codes (enpirate) differ from their filenames
// (en-pirate.json). Webpack splits each into its own chunk.
const localeLoaders: Record<string, () => Promise<{ default: Record<string, any> }>> = {
  es: () => import('./resources/es.json'),
  fr: () => import('./resources/fr.json'),
  it: () => import('./resources/it.json'),
  ja: () => import('./resources/ja.json'),
  ko: () => import('./resources/ko.json'),
  pl: () => import('./resources/pl.json'),
  'pt-br': () => import('./resources/pt-br.json'),
  // i18next reformats 'pt-br' to 'pt-BR' and, with nonExplicitSupportedLngs,
  // may probe the base 'pt' — map both to the Brazilian bundle so the locale
  // resolves instead of silently falling back to English.
  pt: () => import('./resources/pt-br.json'),
  ro: () => import('./resources/ro.json'),
  enpirate: () => import('./resources/en-pirate.json'),
  engymbro: () => import('./resources/en-gymbro.json'),
  enbjj: () => import('./resources/en-bjj.json'),
};

// Custom i18next backend: when a language's resources aren't already bundled
// (everything except `en`), i18next calls read() and we dynamically import
// the matching chunk. Returning {} for unknown codes lets i18next fall back
// to `en` without a console error (e.g. base-language probes like 'pt').
const lazyLoadBackend = {
  type: 'backend' as const,
  init() { /* no options needed */ },
  read(
    language: string,
    _namespace: string,
    callback: (err: unknown, resources: Record<string, any> | null) => void
  ) {
    // Normalize case: i18next requests reformatted codes like 'pt-BR' while
    // our keys are lowercase ('pt-br').
    const loader = localeLoaders[language] || localeLoaders[language.toLowerCase()];
    if (!loader) {
      callback(null, {});
      return;
    }
    loader()
      .then((mod) => callback(null, mod.default))
      .catch((err) => callback(err, null));
  },
};

// Supported languages configuration
export const supportedLanguages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'pt-br', name: 'Português (BR)', flag: '🇧🇷' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  // Fun language variants
  { code: 'enpirate', name: 'Pirate Speak', flag: '🏴‍☠️' },
  { code: 'engymbro', name: 'Gym Bro', flag: '💪' },
  { code: 'enbjj', name: 'BJJ Enthusiast', flag: '🥋' },
];

// Custom language detector that handles pt -> pt-br mapping
const customLanguageDetector = {
  name: 'customDetector',
  lookup() {
    // Get language from localStorage first (user preference)
    const savedLanguage = localStorage.getItem('i18nextLng');
    if (savedLanguage && supportedLanguages.find(lang => lang.code === savedLanguage)) {
      return savedLanguage;
    }

    // Fall back to browser language
    const browserLang = navigator.language || (navigator.languages && navigator.languages[0]) || 'en';
    const languageCode = browserLang.toLowerCase().split('-')[0];
    
    // Special case for Portuguese
    if (languageCode === 'pt') return 'pt-br';
    
    // Check if language is supported
    if (supportedLanguages.find(lang => lang.code === languageCode)) {
      return languageCode;
    }
    
    // Default fallback
    return 'en';
  },
  cacheUserLanguage(lng: string) {
    localStorage.setItem('i18nextLng', lng);
  }
};

// Create instance of language detector and add custom detector
const languageDetector = new LanguageDetectorModule();
languageDetector.addDetector(customLanguageDetector);

// Language detector options
const detectionOptions = {
  order: ['customDetector', 'navigator', 'htmlTag'],
  caches: ['localStorage'],
  excludeCacheFor: ['cimode'],
  checkWhitelist: true,
  lookupLocalStorage: 'i18nextLng'
};

// Initialize i18next
i18n
  .use(lazyLoadBackend)
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    detection: detectionOptions,
    // Only `en` ships bundled; partialBundledLanguages lets it coexist with
    // backend-loaded languages so the fallback is always instant.
    resources: {
      en: { translation: en }
    },
    partialBundledLanguages: true,
    fallbackLng: {
      'enpirate': ['en'],
      'engymbro': ['en'],
      'enbjj': ['en'],
      'default': ['en']
    },
    debug: process.env.NODE_ENV === 'development',
    // Include base 'pt' so i18next's reformatted 'pt-BR' (reduced to 'pt' by
    // the nonExplicit check) is accepted instead of falling back to English.
    // The user-facing language list (supportedLanguages) is unchanged.
    supportedLngs: [...supportedLanguages.map(lang => lang.code), 'pt'],
    nonExplicitSupportedLngs: true,
    // currentOnly avoids probing base languages (e.g. 'pt' for 'pt-br'),
    // which would trigger extra backend reads that resolve to empty
    load: 'currentOnly',
    ns: ['translation'],
    defaultNS: 'translation',

    interpolation: {
      escapeValue: false // React already escapes values
    },

    react: {
      useSuspense: false // Disable suspense to avoid loading states
    }
  });

export default i18n;